import type {
    CSSProperties,
    FC,
    KeyboardEvent,
    PointerEvent,
    ReactNode
} from 'react'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useElementWidth } from '@/hooks/use-element-width'
import { formatTick, formatValue, niceScale } from '@/components/charts/chart-utils'

interface AreaChartProps {
    title: string
    labels: Array<string>
    values: Array<number>
    unit: string
    seriesName: string
    height?: number
    className?: string
}

const MARGIN = { top: 14, right: 40, bottom: 28, left: 40 }
const GRID = '#26231F'
const BASELINE = '#3A362F'
const MUTE = '#8A8478'
const INK = '#ECE6D8'
const SURFACE = '#0A0908'

/**
 * A single series as a 1.5px line over a flat area fill. The line draws itself when the parent
 * block's data-reveal switches to "in". Hover or arrow keys move a crosshair with a tooltip.
 */
export const AreaChart: FC<AreaChartProps> = ({ title, labels, values, unit, seriesName, height = 220, className }): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const width = useElementWidth(containerRef, 560)
    const [active, setActive] = useState<number | null>(null)

    const scale = niceScale(values, 4)
    const plotWidth = Math.max(40, width - MARGIN.left - MARGIN.right)
    const plotHeight = height - MARGIN.top - MARGIN.bottom
    const count = values.length
    const span = scale.max - scale.min || 1
    const total = values.reduce((sum, value) => sum + value, 0)

    const x = (index: number): number => MARGIN.left + (count <= 1 ? plotWidth / 2 : (index / (count - 1)) * plotWidth)
    const y = (value: number): number => MARGIN.top + plotHeight - ((value - scale.min) / span) * plotHeight

    const line = values.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join('')
    const area = `${line}L${x(count - 1).toFixed(1)},${y(scale.min).toFixed(1)}L${x(0).toFixed(1)},${y(scale.min).toFixed(1)}Z`
    const last = count - 1
    const running = values.map((_, index) => values.slice(0, index + 1).reduce((sum, value) => sum + value, 0))

    const updateFromPointer = (event: PointerEvent<SVGRectElement>): void => {
        const rect = event.currentTarget.getBoundingClientRect()
        const ratio = (event.clientX - rect.left) / Math.max(1, rect.width)

        setActive(Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1)))))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault()
            event.stopPropagation()

            const step = event.key === 'ArrowRight' ? 1 : -1

            setActive(current => Math.min(count - 1, Math.max(0, (current ?? (step > 0 ? -1 : count)) + step)))
        }
        else if (event.key === 'Escape') {
            setActive(null)
        }
    }

    const tooltipLeft = active === null ? 0 : x(active)
    const flip = tooltipLeft > width - 200

    return (
        <div className={cn('w-full', className)}>
            <div
                ref={containerRef}
                tabIndex={0}
                role='group'
                aria-label={`${title}. Use the left and right arrow keys to read each year.`}
                onKeyDown={onKeyDown}
                onBlur={() => setActive(null)}
                className='relative w-full select-none'
                style={{ height }}
            >
                <svg width={width} height={height} className='block overflow-visible' aria-hidden='true'>
                    {scale.ticks.map(tick => (
                        <g key={tick}>
                            <line
                                x1={MARGIN.left}
                                x2={MARGIN.left + plotWidth}
                                y1={y(tick)}
                                y2={y(tick)}
                                stroke={tick === scale.min ? BASELINE : GRID}
                                strokeWidth={1}
                                shapeRendering='crispEdges'
                            />

                            <text x={MARGIN.left - 10} y={y(tick)} dy='0.32em' textAnchor='end' fill={MUTE} className='font-mono text-[10px] tracking-[0.08em]'>
                                {formatTick(tick)}
                            </text>
                        </g>
                    ))}

                    {labels.map((label, index) => (width >= 480 || index % 2 === 0 || index === last) ? (
                        <text
                            key={label}
                            x={x(index)}
                            y={height - 8}
                            textAnchor='middle'
                            fill={MUTE}
                            className='font-mono text-[10px] tracking-[0.1em]'
                        >
                            {label}
                        </text>
                    ) : null)}

                    <path d={area} fill='rgba(236, 230, 216, 0.07)' className='reveal-cell' style={{ '--delay': '700ms' } as CSSProperties} />

                    <path d={line} fill='none' stroke={INK} strokeWidth={1.5} strokeLinejoin='round' strokeLinecap='round' pathLength={1} className='draw-path' />

                    <g className='reveal-cell' style={{ '--delay': '1500ms' } as CSSProperties}>
                        <circle cx={x(last)} cy={y(values[last])} r={4} fill={INK} stroke={SURFACE} strokeWidth={2} />

                        <text x={x(last) + 10} y={y(values[last])} dy='0.32em' fill={INK} className='font-mono text-[11px] tracking-[0.06em]'>
                            {formatTick(values[last])}
                        </text>
                    </g>

                    {active !== null && (
                        <g>
                            <line x1={x(active)} x2={x(active)} y1={MARGIN.top} y2={MARGIN.top + plotHeight} stroke='rgba(236,230,216,0.35)' strokeWidth={1} shapeRendering='crispEdges' />
                            <line x1={MARGIN.left} x2={MARGIN.left + plotWidth} y1={y(values[active])} y2={y(values[active])} stroke='rgba(236,230,216,0.18)' strokeWidth={1} shapeRendering='crispEdges' />
                            <circle cx={x(active)} cy={y(values[active])} r={4} fill={INK} stroke={SURFACE} strokeWidth={2} />
                        </g>
                    )}

                    <rect
                        x={MARGIN.left}
                        y={MARGIN.top}
                        width={plotWidth}
                        height={plotHeight}
                        fill='transparent'
                        onPointerMove={updateFromPointer}
                        onPointerDown={updateFromPointer}
                        onPointerLeave={() => setActive(null)}
                    />
                </svg>

                {active !== null && (
                    <div
                        className='pointer-events-none absolute top-0 z-10 min-w-[176px] border border-hairline bg-panel px-3 py-2'
                        style={{
                            left: tooltipLeft,
                            transform: flip ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)'
                        }}
                    >
                        <p className='hud text-mute'>
                            {labels[active]}
                        </p>

                        <p className='hud flex items-center justify-between gap-4 text-ink'>
                            <span>
                                {seriesName}
                            </span>

                            <span>
                                {formatValue(values[active], unit)}
                            </span>
                        </p>

                        <p className='hud flex items-center justify-between gap-4 text-mute'>
                            <span>
                                Running total
                            </span>

                            <span>
                                {formatValue(running[active], unit)}
                            </span>
                        </p>
                    </div>
                )}
            </div>

            <table className='sr-only'>
                <caption>
                    {title}. Total {formatValue(total, unit)}.
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>
                            Year
                        </th>

                        <th scope='col'>
                            {seriesName}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {labels.map((label, index) => (
                        <tr key={label}>
                            <th scope='row'>
                                {label}
                            </th>

                            <td>
                                {formatValue(values[index], unit)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
